import { Head, Link } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { track } from "@/lib/analytics"
import { PropertyVideoPhone } from "@/components/public/property-video-phone"
import { DistributionDiagram } from "@/components/public/distribution-diagram"
import {
  EditorialStyles,
  FilmGrain,
  OrnamentRule,
  ScrollProgress,
  SectionEyebrow,
} from "@/components/public/editorial"
import { useEditorialReveal } from "@/hooks/use-editorial-reveal"
import { REAL_EXAMPLES } from "@/data/real-examples"
import type { SeoMeta } from "@/types"

// The boutique LP hero hard-codes the Cheltenham listing (Leighton Road),
// so the distribution diagram pulls the matching example from the shared
// example set so the photos and the diagram stay in sync.
const LP_EXAMPLE =
  REAL_EXAMPLES.find((e) => e.shareUrl.includes("SpXGy1x0R0p6")) ??
  REAL_EXAMPLES[REAL_EXAMPLES.length - 1]

interface Props {
  meta: SeoMeta
}

const CAMPAIGN_SLUG = "boutique-secret-weapon"

const PAIN_POINTS = [
  "You lost an instruction last month to a chain agent whose only edge was 'looking more professional online'. They didn't even have a better fee.",
  "You tried a videographer once. £400 a property, three days to turn around, and the result felt like a corporate explainer. You haven't booked one since.",
  "Every Sunday night you promise yourself: more videos this week. By Wednesday you're back to the same listing carousel because there's no time.",
]

const BENEFITS = [
  {
    title: "Look like a national brand.",
    body: "Cinematic videos make a one-office agency look like a chain, without the chain's marketing budget.",
  },
  {
    title: "Win the next valuation.",
    body: "Walk in with marketing that out-classes the chain agent's pitch, and watch the vendor lean forward.",
  },
  {
    title: "Charge what you're worth.",
    body: "Premium marketing sells premium fees. Vendors pay more when they can see the difference.",
  },
  {
    title: "Minutes, not days.",
    body: "Paste, pick, post. A few minutes start to finish. No videographer brief, no editor revisions, no waiting on a film crew.",
  },
  {
    title: "Every listing, not just trophies.",
    body: "Cheap enough for a £180,000 terrace, polished enough for a £1.8m townhouse. Same tool, same minute.",
  },
  {
    title: "Locked to your brand.",
    body: "Every video is on-brand by default. Make one between viewings on your phone and it still looks like the design team did it.",
  },
]

const STEPS = [
  {
    n: "I",
    title: "Paste",
    body: "Drop your listing URL from Rightmove, Zoopla, OnTheMarket, or your own agency website. We pull the photos, the price, the address, and the description automatically.",
  },
  {
    n: "II",
    title: "Pick",
    body: "Choose a template (Classic, Urban, Family, or Prestige) to match the property and the buyer.",
  },
  {
    n: "III",
    title: "Post",
    body: "Download the video in square, vertical, or landscape, and put it wherever your next vendor is scrolling.",
  },
]

const STATS = [
  { value: "15s", label: "Video length" },
  { value: "4", label: "Templates" },
  { value: "Any", label: "UK listing portal" },
  { value: "£0", label: "Your first video" },
]

const FAQ = [
  {
    q: "I already use Canva.",
    a: "Canva's brilliant for flyers and brochures. But Canva's video editor is a drag-and-drop timeline. You assemble every clip, every title, every transition. QuiteRight starts with your listing URL and ends with a finished video. Different tools, different jobs.",
  },
  {
    q: "I don't have time to learn another tool.",
    a: "Good. There's nothing to learn. Paste a listing, pick a template, post the video. The first one takes about five minutes, the next ones are quicker once you've found the template you like. If you can copy a URL out of your browser, you can use this.",
  },
  {
    q: "Will this actually look professional?",
    a: "See for yourself. We'll make your first video on the house. Pick any of your current listings, paste the URL, watch what comes back. If the result isn't something you'd happily put on your Instagram feed, you've spent nothing and we've learned what to fix.",
  },
  {
    q: "What's the catch?",
    a: "There isn't one. Your first video is genuinely free, no card required, no trial countdown, no upsell. We're betting that once you see what your listings look like as video, you'll want a second one. That's where we make our money. If we're wrong, you keep your free video and walk away.",
  },
  {
    q: "Who's behind QuiteRight?",
    a: "James, primarily (see the note above). We're a small UK team building one thing well: video for property listings. Not a CRM, not a marketing suite, not trying to sell you ten products. Just the one.",
  },
]

// Identical CTA across all 3 placements + the sticky bar — DO NOT vary
const CTA_BUTTON_LABEL = "Get my first video free"
const CTA_MICROCOPY = "No card. About five minutes. One video, yours to keep."

interface CtaProps {
  variant: "hero" | "mid" | "final" | "sticky"
  tone?: "light" | "dark"
}

function CallToAction({ variant, tone = "light" }: CtaProps) {
  const onClick = () => {
    // track() forwards cta_click → Meta `Lead` automatically via the
    // META_STANDARD_EVENTS map in @/lib/analytics, so no manual fbq call.
    track("cta_click", { campaign: CAMPAIGN_SLUG, placement: variant })
  }

  const microcopyTone =
    tone === "dark" ? "text-stone/80" : "text-warm-grey"

  return (
    <div className="flex flex-col items-center">
      <Link
        href="/sign-up"
        onClick={onClick}
        data-cta-pulse
        className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-none bg-red px-10 py-5 text-base font-semibold uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_-18px_rgba(196,30,42,0.65)] transition-all duration-300 hover:bg-deep-red hover:shadow-[0_22px_50px_-15px_rgba(196,30,42,0.8)] sm:w-auto sm:text-[15px]"
      >
        {/* Subtle inner highlight */}
        <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/15 to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-90" />
        <span className="relative">{CTA_BUTTON_LABEL}</span>
        <span aria-hidden className="relative ml-3 transition-transform duration-300 group-hover:translate-x-1">
          →
        </span>
      </Link>
      <p className={`mt-4 text-center text-[11px] font-medium uppercase tracking-[0.18em] ${microcopyTone}`}>
        {CTA_MICROCOPY}
      </p>
    </div>
  )
}

/**
 * Hero stage — full-bleed charcoal screening-room with the headline stacked
 * over the live PropertyVideoPhone. The phone deliberately peeks below the
 * fold on most viewports to create a "what's that?" curiosity gap.
 */
function HeroStage() {
  return (
    <section
      className="relative overflow-hidden bg-charcoal text-white"
      data-reveal
    >
      {/* Atmospheric vignette + soft red spotlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 50% 45%, rgba(196,30,42,0.18) 0%, rgba(196,30,42,0.05) 30%, rgba(26,26,26,0) 65%), radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)",
        }}
      />
      <FilmGrain />
      {/* Top hairline accent */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-2xl px-6 pt-14 pb-20 text-center md:pt-20 md:pb-28">
        {/* Wordmark */}
        <div data-reveal-stagger="1" className="font-display text-[22px] font-bold tracking-tight">
          <span className="text-cream">Quite</span>
          <span className="text-red">Right.</span>
        </div>

        {/* Eyebrow */}
        <div data-reveal-stagger="2" className="mt-12">
          <SectionEyebrow label="Video marketing for independent agents" />
        </div>

        {/* H1 */}
        <h1
          data-reveal-stagger="3"
          className="mt-8 font-display text-[40px] font-bold leading-[1.05] tracking-tight text-cream sm:text-[56px] md:text-[68px]"
        >
          The secret weapon
          <br />
          for winning more
          <br />
          instructions
          <span className="text-red">.</span>
        </h1>

        {/* Subhead */}
        <p
          data-reveal-stagger="4"
          className="mx-auto mt-7 max-w-xl text-[15px] leading-relaxed text-stone/80 sm:text-base"
        >
          Turn any listing into a cinematic 15-second video. Rightmove, Zoopla, your own
          website, anywhere. Your first one is on us, no card required.
        </p>

        {/* CTA */}
        <div data-reveal-stagger="5" className="mt-10 flex justify-center">
          <CallToAction variant="hero" tone="dark" />
        </div>

        {/* Tracked-caps "see for yourself" cue, sitting just above the phone */}
        <div data-reveal-stagger="6" className="mt-16 mb-6">
          <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">
            ⌄ See it for yourself
          </p>
        </div>

        {/* Live product video — the centrepiece. Wrapped in a soft red glow halo. */}
        <div
          data-reveal-stagger="7"
          className="relative mx-auto"
        >
          {/* Halo glow underneath the phone */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-12 -inset-y-8"
            style={{
              background:
                "radial-gradient(ellipse 60% 50% at 50% 55%, rgba(196,30,42,0.35) 0%, rgba(196,30,42,0.08) 35%, transparent 70%)",
              filter: "blur(20px)",
            }}
          />
          <div className="relative">
            <PropertyVideoPhone
              videoUrl="https://dudpw00jtfdjq.cloudfront.net/videos/16/SpXGy1x0R0p6/zhegqQwTywkh.mp4"
              label="Real QuiteRight video for a four-bedroom property on Leighton Road, Cheltenham, listed by Sam Ray Property at £525,000"
              width={260}
            />
          </div>
          {/* Listing caption */}
          <div className="mx-auto mt-8 max-w-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">
              A real listing, on QuiteRight
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-stone/75">
              A real four-bedroom in Cheltenham. £525,000. Listed by{" "}
              <a
                href="https://www.samrayproperty.co.uk/properties-for-sale/property/12275817-leighton-road-cheltenham"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-cream underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold"
              >
                Sam Ray Property
              </a>
              . The video took a few minutes from the listing photos. Tap the speaker to hear it.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom hairline accent + double rule */}
      <div aria-hidden className="absolute inset-x-0 bottom-[6px] h-px bg-stone/15" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] bg-red" />
    </section>
  )
}

/**
 * Stat strip — narrow horizontal bar with serif numerals separated by hairline
 * verticals. Sits directly under the hero, no padding above, so it reads as
 * the "specifications" plate of an auction catalogue entry.
 */
function StatStrip() {
  return (
    <section className="bg-cream py-10" data-reveal>
      <div className="mx-auto max-w-3xl px-6">
        <div className="grid grid-cols-2 gap-y-8 sm:grid-cols-4 sm:gap-y-0">
          {STATS.map((stat, i) => (
            <div
              key={stat.label}
              className={`flex flex-col items-center text-center ${
                i > 0 ? "sm:border-l sm:border-stone" : ""
              }`}
            >
              <span className="font-display text-[36px] font-bold leading-none tracking-tight text-charcoal sm:text-[44px]">
                {stat.value}
                <span className="text-red">.</span>
              </span>
              <span className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-warm-grey">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * Pain agitation — three numbered editorial paragraphs with serif Roman drop-caps.
 */
function PainSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="The problem" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          You&rsquo;re being out-marketed.
          <br />
          Not out-sold
          <span className="text-red">.</span>
        </h2>

        <div className="mt-16 space-y-12">
          {PAIN_POINTS.map((point, i) => {
            const numerals = ["I", "II", "III"]
            return (
              <div key={i} className="flex gap-6">
                <span className="flex-shrink-0 font-display text-[42px] font-bold italic leading-none text-red sm:text-[52px]">
                  {numerals[i]}
                </span>
                <div className="flex-1">
                  <div className="mt-2 h-px w-12 bg-charcoal/20" />
                  <p className="mt-4 text-[16px] leading-[1.7] text-slate sm:text-[17px]">
                    {point}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-20">
          <OrnamentRule />
        </div>
      </div>
    </section>
  )
}

/**
 * Distribution stage — visual diagram of photos → video → channels.
 * Same component the homepage uses.
 */
function DistributionStage() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <SectionEyebrow label="Listing in. Posts out." />
          <h2 className="mt-6 font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
            Not a video maker.
            <br />
            A publishing pipeline
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-[15px] leading-[1.7] text-slate sm:text-[16px]">
            Most tools stop at &ldquo;here&rsquo;s your video, good luck&rdquo;. QuiteRight
            takes the photos from your listing, renders the cinematic ad, then publishes the
            right cut to every channel that matters. No downloads, no re-uploads, no manual
            scheduling.
          </p>
        </div>

        <div className="mt-16">
          <DistributionDiagram example={LP_EXAMPLE} />
        </div>

        <div className="mt-20">
          <OrnamentRule />
        </div>
      </div>
    </section>
  )
}

/**
 * How it works — three steps with elegant Roman numerals on the left.
 */
function HowItWorksSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="How it works" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Three steps.
          <br />
          No learning curve
          <span className="text-red">.</span>
        </h2>

        <ol className="mt-16 space-y-10">
          {STEPS.map((step) => (
            <li
              key={step.n}
              className="group relative flex gap-6 border-b border-charcoal/10 pb-10 last:border-b-0 last:pb-0"
            >
              <span className="flex-shrink-0 font-display text-[64px] font-bold italic leading-none text-charcoal/85 transition-colors group-hover:text-red sm:text-[72px]">
                {step.n}
              </span>
              <div className="flex-1 pt-2">
                <p className="font-display text-[22px] font-bold text-charcoal sm:text-[26px]">
                  {step.title}
                  <span className="text-red">.</span>
                </p>
                <p className="mt-3 text-[15px] leading-[1.7] text-slate sm:text-[16px]">
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/**
 * Benefits — six numbered editorial benefit statements in a single column.
 * Each has a large serif number, a bold title, and a short body.
 */
function BenefitsSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="Why it works" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Six reasons boutique agents
          <br />
          are signing up
          <span className="text-red">.</span>
        </h2>

        <ul className="mt-16 space-y-10">
          {BENEFITS.map((benefit, i) => {
            const num = String(i + 1).padStart(2, "0")
            return (
              <li
                key={benefit.title}
                className="group relative grid grid-cols-[auto_1fr] gap-6"
              >
                <div className="flex flex-col items-center">
                  <span className="font-display text-[28px] font-bold leading-none text-red transition-transform duration-300 group-hover:scale-105">
                    {num}
                  </span>
                  <span className="mt-3 h-full w-px flex-1 bg-charcoal/15" />
                </div>
                <div className="pb-2 pt-1">
                  <p className="font-display text-[22px] font-bold text-charcoal sm:text-[24px]">
                    {benefit.title}
                  </p>
                  <p className="mt-2 text-[15px] leading-[1.7] text-slate sm:text-[16px]">
                    {benefit.body}
                  </p>
                </div>
              </li>
            )
          })}
        </ul>

        <div className="mt-20 flex justify-center">
          <CallToAction variant="mid" />
        </div>
      </div>
    </section>
  )
}

/**
 * Mid CTA stage — full-bleed charcoal panel between sections, marquee-style.
 */
function MidCtaStage() {
  return (
    <section className="relative overflow-hidden bg-charcoal text-white" data-reveal>
      {/* Atmospheric red wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(196,30,42,0.16) 0%, rgba(196,30,42,0.04) 35%, transparent 70%)",
        }}
      />
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-2xl px-6 py-20 text-center md:py-28">
        <SectionEyebrow label="Try one on us" tone="gold" />
        <h2 className="mt-6 font-display text-[36px] font-bold leading-[1.1] tracking-tight text-cream sm:text-[48px]">
          Your photos are doing nothing.
          <br />
          Try the alternative
          <span className="text-red">.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-md text-[15px] leading-relaxed text-stone/80 sm:text-base">
          One free video on any of your current listings. Pick the most expensive one and
          see how it looks before you commit a penny.
        </p>
        <div className="mt-12 flex justify-center">
          <CallToAction variant="mid" tone="dark" />
        </div>
      </div>
    </section>
  )
}

/**
 * Trust panel — two "personal letter" cards. The founder card gets the gold
 * ruled border treatment, large serif quotation mark, italic signature.
 */
function TrustSection() {
  return (
    <section className="bg-cream px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-3xl">
        <SectionEyebrow label="The team" tone="gold" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[42px]">
          A small UK team
          <br />
          building one thing well
          <span className="text-red">.</span>
        </h2>

        <div className="mt-16 grid gap-10 md:grid-cols-2">
          {/* Founder's letter card */}
          <article className="relative bg-white p-8 shadow-[0_20px_60px_-30px_rgba(26,26,26,0.35)] md:p-10">
            {/* Gold double-ruled border accent */}
            <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-gold" />
            <div aria-hidden className="absolute inset-x-0 top-[5px] h-px bg-gold/40" />

            {/* Large serif quotation mark */}
            <div className="font-display text-[88px] font-bold leading-none text-gold/60">
              &ldquo;
            </div>

            <p className="-mt-10 font-display text-[20px] font-bold italic text-charcoal">
              Hi, I&rsquo;m James.
            </p>

            <p className="mt-5 text-[14px] leading-[1.75] text-slate">
              I built QuiteRight after watching a friend, a brilliant independent agent in Bath,
              lose three instructions in a row to a chain agent who&rsquo;d spent £40,000 on a
              videographer team. The product they shipped was fine. The product my friend could
              have shipped, for free, in a few minutes, would have been every bit as good.
              He just had no way to make it.
            </p>

            <p className="mt-4 text-[14px] leading-[1.75] text-slate">
              That&rsquo;s the gap QuiteRight closes. Try one video on us. If it isn&rsquo;t quite
              right, you&rsquo;ve lost nothing.
            </p>

            {/* Italic signature line + horizontal rule */}
            <div className="mt-8 flex items-end gap-4">
              <div className="h-px flex-1 bg-charcoal/20" />
              <p className="font-display text-[20px] italic text-charcoal">James</p>
            </div>
            <p className="mt-1 text-right text-[10px] font-semibold uppercase tracking-[0.22em] text-warm-grey">
              Founder
            </p>
          </article>

          {/* Honest declaration card */}
          <article className="relative bg-white p-8 shadow-[0_20px_60px_-30px_rgba(26,26,26,0.35)] md:p-10">
            <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-charcoal" />
            <div aria-hidden className="absolute inset-x-0 top-[5px] h-px bg-charcoal/30" />

            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-warm-grey">
              An honest note
            </p>

            <p className="mt-4 font-display text-[22px] font-bold leading-tight text-charcoal sm:text-[24px]">
              Why we&rsquo;re not showing you a customer logo wall
              <span className="text-red">.</span>
            </p>

            <p className="mt-6 text-[14px] leading-[1.75] text-slate">
              Because we don&rsquo;t have one yet. You&rsquo;re early. So are we.
            </p>
            <p className="mt-4 text-[14px] leading-[1.75] text-slate">
              What we do have: a product that turns any property listing URL into a 15-second
              cinematic video in a few minutes. Try it on your next listing. We&rsquo;ll cover
              the first one.
            </p>
            <p className="mt-4 text-[14px] leading-[1.75] text-slate">
              If we&rsquo;re worth your time, you&rsquo;ll know after one video. If we&rsquo;re
              not, you&rsquo;ve lost nothing, and we&rsquo;ll have learned something.
            </p>
          </article>
        </div>
      </div>
    </section>
  )
}

/**
 * FAQ — restyled with serif Q. markers and a more refined hover/focus state.
 */
function FaqSection() {
  return (
    <section className="bg-white px-6 py-20 md:py-28" data-reveal>
      <div className="mx-auto max-w-2xl">
        <SectionEyebrow label="Questions" />
        <h2 className="mt-6 text-center font-display text-[34px] font-bold leading-tight tracking-tight text-charcoal sm:text-[44px]">
          Questions, answered
          <span className="text-red">.</span>
        </h2>

        <dl className="mt-16">
          {FAQ.map((item, i) => (
            <details
              key={i}
              className="group border-b border-charcoal/10 py-7 last:border-b-0"
            >
              <summary className="flex cursor-pointer items-start gap-5 text-left [&::-webkit-details-marker]:hidden">
                <span className="mt-1 font-display text-[26px] font-bold italic leading-none text-red transition-transform duration-300 group-open:scale-110">
                  Q.
                </span>
                <dt className="flex-1 font-display text-[18px] font-bold leading-snug text-charcoal sm:text-[20px]">
                  {item.q}
                </dt>
                <span
                  aria-hidden
                  className="mt-2 inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-charcoal/20 text-charcoal/60 transition-all duration-300 group-open:rotate-45 group-open:border-red group-open:text-red"
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                    <path d="M8 3v10M3 8h10" />
                  </svg>
                </span>
              </summary>
              <dd className="mt-5 pl-12 text-[14px] leading-[1.8] text-slate sm:text-[15px]">
                {item.a}
              </dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  )
}

/**
 * Final CTA stage — full-bleed charcoal cinema close. Bigger than the mid CTA.
 */
function FinalCtaStage() {
  return (
    <section
      id="final-cta"
      className="relative overflow-hidden bg-charcoal text-white"
      data-reveal
    >
      {/* Atmospheric red spotlight */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 75% 60% at 50% 45%, rgba(196,30,42,0.22) 0%, rgba(196,30,42,0.05) 38%, transparent 72%), radial-gradient(ellipse 100% 80% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)",
        }}
      />
      <FilmGrain />
      <div aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-red" />

      <div className="relative mx-auto max-w-2xl px-6 py-24 text-center md:py-32">
        <SectionEyebrow label="One video away" tone="gold" />

        <h2 className="mt-6 font-display text-[42px] font-bold leading-[1.05] tracking-tight text-cream sm:text-[60px] md:text-[72px]">
          Your next instruction
          <br />
          is one video away
          <span className="text-red">.</span>
        </h2>

        <p className="mx-auto mt-6 max-w-md text-[15px] leading-relaxed text-stone/80 sm:text-base">
          Try it on your current listing. Yours, free, in about five minutes.
        </p>

        <div className="mt-12 flex justify-center">
          <CallToAction variant="final" tone="dark" />
        </div>

        {/* Wordmark anchor */}
        <div className="mt-20 flex flex-col items-center gap-4">
          <OrnamentRule tone="red" />
          <div className="font-display text-[18px] font-bold tracking-tight">
            <span className="text-cream">Quite</span>
            <span className="text-red">Right.</span>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Sticky mobile CTA — appears after the user has scrolled past the hero
 * and disappears when they reach the final CTA. Mobile only.
 */
function StickyMobileCta() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      const heroPassed = window.scrollY > window.innerHeight * 0.7
      const finalCtaEl = document.getElementById("final-cta")
      const finalCtaTop =
        finalCtaEl?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY
      const beforeFinalCta = finalCtaTop > window.innerHeight * 0.5
      setVisible(heroPassed && beforeFinalCta)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const onClick = () => {
    // track() forwards cta_click → Meta `Lead` automatically.
    track("cta_click", { campaign: CAMPAIGN_SLUG, placement: "sticky" })
  }

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 border-t-2 border-red bg-charcoal/95 px-4 py-3 backdrop-blur-md transition-all duration-300 sm:hidden ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
    >
      <Link
        href="/sign-up"
        onClick={onClick}
        className="flex w-full items-center justify-center gap-2 bg-red px-5 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_-10px_rgba(196,30,42,0.6)]"
      >
        {CTA_BUTTON_LABEL}
        <span aria-hidden>→</span>
      </Link>
      <p className="mt-2 text-center text-[10px] uppercase tracking-[0.16em] text-stone/70">
        No card · About five minutes
      </p>
    </div>
  )
}

export default function LpBoutiqueSecretWeapon({ meta }: Props) {
  useEditorialReveal()

  useEffect(() => {
    // track() forwards lp_view → Meta `ViewContent` automatically;
    // lp_view_15s is custom (PostHog + GA4 only) for the 15-second
    // dwell-time funnel signal.
    track("lp_view", { campaign: CAMPAIGN_SLUG })

    const t = window.setTimeout(() => {
      track("lp_view_15s", { campaign: CAMPAIGN_SLUG })
    }, 15_000)

    return () => window.clearTimeout(t)
  }, [])

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        {meta.image && <meta property="og:image" content={meta.image} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        {meta.image && <meta name="twitter:image" content={meta.image} />}

        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <EditorialStyles />
      <ScrollProgress />

      <main className="min-h-screen bg-cream">
        <HeroStage />
        <StatStrip />
        <PainSection />
        <DistributionStage />
        <HowItWorksSection />
        <BenefitsSection />
        <MidCtaStage />
        <TrustSection />
        <FaqSection />
        <FinalCtaStage />

        {/* Minimal legal footer — single line, anchored to the bottom */}
        <footer className="bg-charcoal py-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] text-stone/60">
            © QuiteRight.{" "}
            <Link href="/privacy" className="text-stone/70 underline decoration-stone/40 underline-offset-4 transition-colors hover:text-cream">
              Privacy
            </Link>
            {" · "}
            <Link href="/terms" className="text-stone/70 underline decoration-stone/40 underline-offset-4 transition-colors hover:text-cream">
              Terms
            </Link>
          </p>
        </footer>
      </main>

      <StickyMobileCta />
    </>
  )
}
